import { useEffect, useState } from "react";
import { Button, Table } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faEye, faEyeSlash, faRotate } from "@fortawesome/free-solid-svg-icons";
import DynamicForm from "@mavichpe/dynamic-forms";

import PageContainer from "@components/layout/PageContainer";
import { showAlert } from "@utils/Alert";
import { API_RESOURCE_CATALOG } from "@utils/Constants";
import { getTenantId } from "@utils/Auth";
import { useAdminContext } from "@/hooks/useAdminContext";
import { fetchSuscription, SuscriptionDataType, updateSuscription } from "@/api/SuscriptionsApi";
import { ApiResourceApi, ApiResourceDataType } from "@/api/ApiResourcesApi";
import {
  buildPublicApiUrl,
  PublicApiThrottledError,
  regeneratePublicApi,
  rotatePublicApiPassword,
} from "@/api/PublicApiAdminApi";
import { ApiManagementFormData } from "@/formTypes/ApiManagementDataType";
import ApiManagementFormConfig from "./form";
import styles from "./style.module.scss";

const ApiManagement = () => {
  const { adminTenantId, adminSuscriptionId, isAdminMode, profileBackUrl } = useAdminContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [suscription, setSuscription] = useState<SuscriptionDataType>({});
  const [resources, setResources] = useState<ApiResourceDataType[]>([]);
  const [tenantId, setTenantId] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const suscriptionId = isAdminMode ? adminSuscriptionId : undefined;
        const data = await fetchSuscription(suscriptionId ?? undefined);
        setSuscription(data);
        setTenantId(isAdminMode ? (adminTenantId ?? "") : await getTenantId());
        await fetchResources();
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    loadData();
  }, []);

  const fetchResources = async () => {
    const { items = [] } = await ApiResourceApi.fetchAll();
    setResources(items);
  };

  const isApiEnabled = suscription?.api_enabled === true;

  const updateProfile = async (name: keyof ApiManagementFormData, value: any) => {
    if (!suscription?.id) return;
    setIsLoading(true);
    try {
      await updateSuscription(suscription.id, { [name]: value });
      setSuscription({ ...suscription, [name]: value });
      showAlert("success", "Informacion actualizada");
    } finally {
      setIsLoading(false);
    }
  };

  /** La fila de ApiResource se crea la primera vez que se activa el recurso. */
  const toggleResource = async (name: string, label: string, enabled: boolean) => {
    setIsLoading(true);
    try {
      const existing = resources.find((r) => r.name === name);
      if (existing?.id) {
        await ApiResourceApi.update(existing.id, { enabled });
      } else {
        await ApiResourceApi.create({ name, label, enabled });
      }
      await fetchResources();
      showAlert("success", enabled ? `API de ${label} activada` : `API de ${label} desactivada`);
    } catch (e) {
      showAlert("danger", e instanceof Error ? e.message : "No fue posible actualizar el API");
    } finally {
      setIsLoading(false);
    }
  };

  const regenerate = async () => {
    setIsLoading(true);
    try {
      const { itemsCount } = await regeneratePublicApi();
      await fetchResources();
      showAlert("success", `El API se regenero con ${itemsCount} registros`);
    } catch (e) {
      if (e instanceof PublicApiThrottledError) {
        showAlert("warning", e.message);
      } else {
        showAlert("danger", e instanceof Error ? e.message : "No fue posible regenerar el API");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const rotatePassword = async () => {
    setIsLoading(true);
    try {
      const { username, password, updatedAt } = await rotatePublicApiPassword();
      setSuscription({
        ...suscription,
        api_username: username,
        api_password: password,
        api_password_updated_at: updatedAt ?? undefined,
      });
      setShowPassword(true);
      showAlert("success", "Credenciales generadas");
    } catch (e) {
      showAlert("danger", e instanceof Error ? e.message : "No fue posible generar las credenciales");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showAlert("success", `${label} copiado`);
    } catch {
      showAlert("warning", "No fue posible copiar al portapapeles");
    }
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "Nunca";
    const date = new Date(value);
    return isNaN(date.getTime()) ? "Nunca" : date.toLocaleString();
  };

  const renderResourceRows = () =>
    API_RESOURCE_CATALOG.map((entry) => {
      const resource = resources.find((r) => r.name === entry.name);
      const enabled = resource?.enabled === true;
      const url = buildPublicApiUrl(tenantId, entry.name);
      const hasError = resource?.last_status === "error";

      return (
        <tr key={entry.name} className={styles.row}>
          <td>
            <div>{entry.label}</div>
            <small className={styles.muted}>{entry.description}</small>
          </td>
          <td>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                data-testid={`api-resource-${entry.name}`}
                checked={enabled}
                disabled={isLoading}
                onChange={(e) => toggleResource(entry.name, entry.label, e.target.checked)}
              />
            </div>
          </td>
          <td>
            {enabled && url ? (
              <div className={styles.urlCell}>
                <span className={styles.url} data-testid={`api-url-${entry.name}`}>{url}</span>
                <Button
                  size="sm"
                  color="light"
                  data-testid={`api-copy-url-${entry.name}`}
                  onClick={() => copyToClipboard(url, "URL")}
                >
                  <FontAwesomeIcon icon={faCopy} />
                </Button>
              </div>
            ) : (
              <span className={styles.muted}>—</span>
            )}
          </td>
          <td data-testid={`api-last-generated-${entry.name}`}>
            {hasError ? (
              <span className={styles.error}>
                Error: {resource?.last_error || "revisa la ultima generacion"}
              </span>
            ) : (
              <>
                {formatDate(resource?.last_generated_at)}
                {resource?.items_count != null && (
                  <>
                    {" "}
                    <small className={styles.muted}>({resource.items_count} registros)</small>
                  </>
                )}
              </>
            )}
          </td>
          <td>
            <div className={styles.actions}>
              <Button
                size="sm"
                color="secondary"
                disabled={!enabled || isLoading}
                data-testid={`api-regenerate-${entry.name}`}
                onClick={regenerate}
              >
                <FontAwesomeIcon icon={faRotate} /> Regenerar ahora
              </Button>
            </div>
          </td>
        </tr>
      );
    });

  return (
    <PageContainer title="API Externa" backUrl={profileBackUrl}>
      <div className={styles.intro}>
        <h3>API externa de solo lectura</h3>
        <p>
          Permite que un sistema externo (una pagina web, una tienda en linea o el ERP de un
          cliente) consulte la informacion de tu empresa. Ten en cuenta:
        </p>
        <ul className={styles.optionsList}>
          <li>El API es de <b>solo lectura</b>: no permite crear ni modificar informacion.</li>
          <li>La informacion se actualiza <b>una vez al dia</b>. Usa "Regenerar ahora" si necesitas publicar un cambio antes.</li>
          <li>Los precios se entregan <b>sin impuesto</b>. Quien consume el API hace el calculo final.</li>
        </ul>
      </div>

      <DynamicForm<ApiManagementFormData>
        externalFormState={suscription}
        formConfig={ApiManagementFormConfig}
        isLoading={isLoading}
        onInputChange={updateProfile}
      />

      {isApiEnabled && (
        <>
          <Table bordered responsive className="mt-3" data-testid="api-resources-table">
            <thead>
              <tr>
                <th>API</th>
                <th>Activa</th>
                <th>URL</th>
                <th>Ultima generacion</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>{renderResourceRows()}</tbody>
          </Table>

          <div className={styles.credentials} data-testid="api-credentials">
            <h5>Credenciales de acceso</h5>
            <p className={styles.muted}>
              Quien consuma el API debe enviarlas como autenticacion basica (usuario y
              contrasena). Si las regeneras, las integraciones que ya las usaban dejaran de
              funcionar hasta que las actualices.
            </p>

            {suscription?.api_username ? (
              <>
                <p>
                  <b>Usuario:</b>{" "}
                  <span className={styles.credentialValue} data-testid="api-username">
                    {suscription.api_username}
                    <Button
                      size="sm"
                      color="light"
                      onClick={() => copyToClipboard(`${suscription.api_username}`, "Usuario")}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </Button>
                  </span>
                </p>
                <p>
                  <b>Contrasena:</b>{" "}
                  <span className={styles.credentialValue}>
                    <span data-testid="api-password">
                      {showPassword ? suscription.api_password : "••••••••••••"}
                    </span>
                    <Button
                      size="sm"
                      color="light"
                      data-testid="api-toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </Button>
                    <Button
                      size="sm"
                      color="light"
                      onClick={() => copyToClipboard(`${suscription.api_password}`, "Contrasena")}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </Button>
                  </span>
                </p>
                <p className={styles.muted}>
                  Actualizada: {formatDate(suscription.api_password_updated_at)}
                </p>
              </>
            ) : (
              <p className={styles.muted}>Todavia no has generado credenciales.</p>
            )}

            <Button
              color="success"
              disabled={isLoading}
              data-testid="api-rotate-password"
              onClick={rotatePassword}
            >
              {suscription?.api_username ? "Regenerar contrasena" : "Generar credenciales"}
            </Button>
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default ApiManagement;
